import i18n from 'lib/i18next';
import { displayAlert } from 'redux-implementations/alerts';
import { AlertTypes } from 'types/State';
import authSignInWithGoogle from 'lib/UseCases/signInWithGoogle';
import authSignInWithFacebook from 'lib/UseCases/signInWithFacebook';
import authCreateAccountByEmail from 'lib/UseCases/createAccountByEmail';
import authSignInWithEmailAndPassword from 'lib/UseCases/signInWithEmailAndPassword';
import { DialogNames, dialogClosed } from 'redux-implementations/dialog';
import {
  sendPasswordResetEmail as authSendPasswordResetEmail,
  signOut as authSignOut,
} from 'lib/Auth';
import { firebaseErrorCodeToTranslationMap } from 'lib/AuthError';
import { signedIn as sessionSignedIn, signedOut } from 'redux-implementations/session';
import { toPlainObject } from 'lodash';
import { Dispatch } from 'redux';
import { updateUser } from 'lib/AutolistAPI';
import { CreateEmailAccountParams } from 'types/Auth';

export function signOut() {
  return async (dispatch: Dispatch): Promise<void> => {
    await authSignOut();
    dispatch(signedOut());
  };
}

function getErrorMessage(firebaseCode: string): string {
  return (
    i18n.t((firebaseErrorCodeToTranslationMap as any)[firebaseCode]) ||
    i18n.t('auth.errors.generic')
  );
}

export function sendPasswordResetEmail(email: string) {
  return async (dispatch: Dispatch<any>): Promise<void> => {
    try {
      await authSendPasswordResetEmail(email);
      dispatch(dialogClosed(DialogNames.Auth));
      dispatch(
        displayAlert({
          type: AlertTypes.Success,
          message: i18n.t('auth.resetPassword.confirmation'),
        })
      );
    } catch (e) {
      dispatch(
        displayAlert({
          type: AlertTypes.Error,
          message: getErrorMessage(e.code),
          debug: toPlainObject(e),
        })
      );
    }
  };
}

export function signInWithGoogle() {
  return async (dispatch: Dispatch<any>): Promise<void> => {
    try {
      const userInfo = await authSignInWithGoogle();
      dispatch(sessionSignedIn(userInfo));
      dispatch(dialogClosed(DialogNames.Auth));
    } catch (e) {
      dispatch(
        displayAlert({
          type: AlertTypes.Error,
          message: getErrorMessage(e.code),
          debug: toPlainObject(e).toJSON(),
        })
      );
    }
  };
}

export function signInWithFacebook() {
  return async (dispatch: Dispatch<any>): Promise<void> => {
    try {
      const userInfo = await authSignInWithFacebook();
      dispatch(sessionSignedIn(userInfo));
      dispatch(dialogClosed(DialogNames.Auth));
    } catch (e) {
      dispatch(
        displayAlert({
          type: AlertTypes.Error,
          message: getErrorMessage(e.code),
          debug: toPlainObject(e).toJSON(),
        })
      );
    }
  };
}

export function createAccountByEmail({ email, password, acceptEmails }: CreateEmailAccountParams) {
  return async (dispatch: Dispatch<any>): Promise<void> => {
    try {
      const userInfo = await authCreateAccountByEmail(email, password);
      await updateUser({
        accepts_price_emails: acceptEmails,
        accepts_search_emails: acceptEmails,
        accepts_alert_emails: acceptEmails,
      });
      dispatch(sessionSignedIn(userInfo));
      dispatch(dialogClosed(DialogNames.Auth));
    } catch (e) {
      dispatch(
        displayAlert({
          type: AlertTypes.Error,
          message: getErrorMessage(e.code),
          debug: toPlainObject(e),
        })
      );
    }
  };
}

export function signInWithEmailAndPassword(email: string, password: string) {
  return async (dispatch: Dispatch<any>): Promise<void> => {
    try {
      const userInfo = await authSignInWithEmailAndPassword(email, password);
      dispatch(sessionSignedIn(userInfo));
      dispatch(dialogClosed(DialogNames.Auth));
    } catch (e) {
      dispatch(
        displayAlert({
          type: AlertTypes.Error,
          message: getErrorMessage(e.code),
          debug: JSON.stringify(toPlainObject(e)),
        })
      );
    }
  };
}
